import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { ConversationThread } from "@/components/ConversationThread";
import type { Conversation, Message } from "@/data/communicationTypes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Search, MessageSquare, Phone, FileText, Briefcase, Target,
  User, Clock, Flame, Thermometer, Snowflake, AlertTriangle,
  CheckCircle, PhoneCall, ArrowRight, Calendar, TrendingUp,
  Zap, Send, Bot, Sparkles, Play, Pause, Edit3, Copy,
  ChevronRight, Shield, Radio,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitKpiStrip, CockpitFilterBar, CockpitSearch } from "@/components/cockpit/CockpitShell";
import { differenceInHours, differenceInDays, parseISO, format } from "date-fns";
import { toast } from "sonner";
import {
  getSequenceStatus, generateAIResponse, getBookingOptimization,
  TONE_CONFIG, MODE_CONFIG,
  type SequenceStatus, type AIResponseSuggestion, type BookingOptimization,
} from "@/data/conversationAutomation";
import { resolveProfileDisplayName } from "@/lib/profileDisplayName";

/* ═══════ ENGAGEMENT INTELLIGENCE ═══════ */

type EngagementStatus = "hot" | "warm" | "cold" | "unresponsive";

const ENGAGEMENT_CONFIG: Record<EngagementStatus, { label: string; icon: React.ElementType; color: string; bg: string }> = {
  hot:          { label: "Hot",          icon: Flame,       color: "text-warning",       bg: "bg-warning/10 border-warning/30" },
  warm:         { label: "Warm",         icon: Thermometer, color: "text-warning",        bg: "bg-warning/10 border-warning/30" },
  cold:         { label: "Cold",         icon: Snowflake,   color: "text-info",         bg: "bg-info/10 border-info/30" },
  unresponsive: { label: "Unresponsive", icon: AlertTriangle, color: "text-muted-foreground", bg: "bg-muted border-muted" },
};

type ConvoFilter = "all" | "hot" | "warm" | "cold" | "unresponsive" | "unread" | "appointment_set" | "no_response";

interface EnrichedConversation extends Conversation {
  engagement: EngagementStatus;
  lastContactAt: string | null;
  lastInboundAt: string | null;
  hoursSinceLastContact: number;
  hoursSinceLastInbound: number;
  responseTimeAvgMin: number;
  messageCount: number;
  hasAppointment: boolean;
  linkedStage: string | null;
  sequenceStatus: SequenceStatus | null;
}

const repLabel = (value: string | null | undefined) =>
  resolveProfileDisplayName(value, undefined, "Unassigned");

function enrichConversation(conv: Conversation, msgs: Message[], opp: any | null, conversations: Conversation[], allMessages: Message[]): EnrichedConversation {
  const convMsgs = msgs.filter((m) => m.conversationId === conv.id);
  const sorted = convMsgs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const lastMsg = sorted.length > 0 ? sorted[sorted.length - 1] : null;
  const lastInbound = [...sorted].reverse().find((m) => m.direction === "inbound");

  const now = new Date();
  const lastContactAt = lastMsg?.timestamp || null;
  const lastInboundAt = lastInbound?.timestamp || null;
  const hoursSinceLastContact = lastContactAt ? differenceInHours(now, parseISO(lastContactAt)) : 9999;
  const hoursSinceLastInbound = lastInboundAt ? differenceInHours(now, parseISO(lastInboundAt)) : 9999;

  let totalResponseMin = 0;
  let responsePairs = 0;
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].direction === "inbound" && sorted[i + 1].direction === "outbound") {
      const diff = differenceInHours(parseISO(sorted[i + 1].timestamp), parseISO(sorted[i].timestamp));
      totalResponseMin += diff * 60;
      responsePairs++;
    }
  }
  const responseTimeAvgMin = responsePairs > 0 ? Math.round(totalResponseMin / responsePairs) : 0;

  let engagement: EngagementStatus = "warm";
  const hasInboundRecent = hoursSinceLastInbound < 48;
  const hasAnyInbound = !!lastInbound;

  if (!hasAnyInbound && hoursSinceLastContact > 72) engagement = "unresponsive";
  else if (!hasAnyInbound && hoursSinceLastContact > 24) engagement = "cold";
  else if (hasInboundRecent && hoursSinceLastInbound < 12) engagement = "hot";
  else if (hasInboundRecent) engagement = "warm";
  else if (hoursSinceLastInbound > 72) engagement = "cold";
  else if (hoursSinceLastInbound > 168) engagement = "unresponsive";

  const oppStage = opp?.stage || null;
  const hasAppointment = oppStage === "Appointment Scheduled" || oppStage === "Appointment Completed";
  const sequenceStatus = opp ? getSequenceStatus(opp, conversations, allMessages) : null;

  return {
    ...conv,
    engagement,
    lastContactAt,
    lastInboundAt,
    hoursSinceLastContact,
    hoursSinceLastInbound,
    responseTimeAvgMin,
    messageCount: convMsgs.length,
    hasAppointment,
    linkedStage: oppStage,
    sequenceStatus,
  };
}

/* ═══════ MAIN COMPONENT ═══════ */

export default function MessagesPage() {
  const { conversations, messages, salesOpportunities, followUpTasks, setFollowUpTasks } = useAppData();
  const { role } = useRole();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [repFilter, setRepFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<ConvoFilter>("all");
  const [activeTab, setActiveTab] = useState("inbox");
  const [followUpDialog, setFollowUpDialog] = useState<string | null>(null);
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [showAIPanel, setShowAIPanel] = useState(true);
  const [aiSuggestion, setAiSuggestion] = useState<AIResponseSuggestion | null>(null);
  const [editedMessage, setEditedMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const isRepRole = role === "sales";
  const currentRep = isRepRole ? "Mike Johnson" : null;

  // Enrich all conversations
  const enriched = useMemo((): EnrichedConversation[] => {
    return conversations.map((c) => {
      const opp = salesOpportunities.find((o) =>
        o.id === c.linkedLeadId || o.id === c.linkedQuoteId
      );
      return enrichConversation(c, messages, opp || null, conversations, messages);
    });
  }, [conversations, messages, salesOpportunities]);

  const reps = useMemo((): string[] => {
    const s = new Set<string>(enriched.map((c) => c.assignedRep).filter(Boolean));
    return Array.from(s).sort();
  }, [enriched]);

  const filtered = useMemo(() => {
    let result = enriched;
    if (isRepRole && currentRep) result = result.filter((c) => c.assignedRep === currentRep);
    if (repFilter !== "all") result = result.filter((c) => c.assignedRep === repFilter);

    switch (statusFilter) {
      case "hot": result = result.filter((c) => c.engagement === "hot"); break;
      case "warm": result = result.filter((c) => c.engagement === "warm"); break;
      case "cold": result = result.filter((c) => c.engagement === "cold"); break;
      case "unresponsive": result = result.filter((c) => c.engagement === "unresponsive"); break;
      case "unread": result = result.filter((c) => c.unreadCount > 0); break;
      case "appointment_set": result = result.filter((c) => c.hasAppointment); break;
      case "no_response": result = result.filter((c) => !c.lastInboundAt); break;
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter((c) =>
        c.contactName.toLowerCase().includes(q) ||
        c.contactPhone.includes(q) ||
        c.contactEmail.toLowerCase().includes(q)
      );
    }
    return result.sort((a, b) => {
      if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
      if (b.unreadCount > 0 && a.unreadCount === 0) return 1;
      const engOrder: Record<EngagementStatus, number> = { hot: 0, warm: 1, cold: 2, unresponsive: 3 };
      const engDiff = engOrder[a.engagement] - engOrder[b.engagement];
      if (engDiff !== 0) return engDiff;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [enriched, search, repFilter, statusFilter, isRepRole, currentRep]);

  const selected = selectedId ? enriched.find((c) => c.id === selectedId) : null;
  const selectedOpp = selected
    ? salesOpportunities.find((o) => o.id === selected.linkedLeadId || o.id === selected.linkedQuoteId)
    : null;

  // Generate AI suggestion when conversation changes
  const currentAISuggestion = useMemo(() => {
    if (!selectedOpp) return null;
    return generateAIResponse(selectedOpp, conversations, messages);
  }, [selectedOpp, conversations, messages]);

  const bookingOpt = useMemo(() => {
    if (!selectedOpp) return null;
    return getBookingOptimization(selectedOpp, conversations, messages);
  }, [selectedOpp, conversations, messages]);

  // KPIs
  const stats = useMemo(() => {
    const totalUnread = enriched.reduce((sum, c) => sum + c.unreadCount, 0);
    const hot = enriched.filter((c) => c.engagement === "hot").length;
    const cold = enriched.filter((c) => c.engagement === "cold").length;
    const unresponsive = enriched.filter((c) => c.engagement === "unresponsive").length;
    const withAppt = enriched.filter((c) => c.hasAppointment).length;
    const automated = enriched.filter((c) => c.sequenceStatus && !c.sequenceStatus.isComplete).length;
    const avgResponseMin = enriched.filter((c) => c.responseTimeAvgMin > 0);
    const avgResp = avgResponseMin.length > 0
      ? Math.round(avgResponseMin.reduce((s, c) => s + c.responseTimeAvgMin, 0) / avgResponseMin.length)
      : 0;
    return { total: enriched.length, totalUnread, hot, cold, unresponsive, withAppt, avgResp, automated };
  }, [enriched]);

  function handleCreateFollowUp() {
    if (!followUpDialog || !selected) return;
    const now = new Date();
    const dueDate = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
    const task = {
      id: `FU-${Date.now()}`,
      opportunityId: selected.linkedLeadId || selected.linkedQuoteId || "",
      type: "rep" as const,
      assignedTo: selected.assignedRep,
      dueDate,
      status: "pending" as const,
      notes: followUpNotes || `Follow up with ${selected.contactName}`,
      createdAt: now.toISOString(),
      disposition: {} as any,
      customerName: selected.contactName,
      customerPhone: selected.contactPhone,
      priority: "medium" as const,
      executionMethod: "call" as const,
    };
    setFollowUpTasks((prev) => [...prev, task]);
    toast.success("Follow-up task created");
    setFollowUpDialog(null);
    setFollowUpNotes("");
  }

  function handleSendAISuggestion() {
    const msg = isEditing ? editedMessage : currentAISuggestion?.message;
    if (!msg) return;
    toast.success("Message sent", { description: msg.substring(0, 60) + "..." });
    setIsEditing(false);
    setEditedMessage("");
  }

  function handleCopyMessage() {
    const msg = isEditing ? editedMessage : currentAISuggestion?.message;
    if (msg) {
      navigator.clipboard.writeText(msg);
      toast.success("Copied to clipboard");
    }
  }

  function formatTimeSince(hours: number): string {
    if (hours < 1) return "< 1h ago";
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  return (
    <CockpitPage>
      {/* ── Masthead ── */}
      <CockpitMasthead
        eyebrow="Sales · Messages"
        title="Conversations"
        right={
          <>
            <HeroMetric label="Total" value={stats.total} hero />
            <HeroDivider />
            <HeroMetric label="Unread" value={stats.totalUnread} tone={stats.totalUnread > 0 ? "warn" : "default"} />
            <HeroDivider />
            <HeroMetric label="Hot Leads" value={stats.hot} tone={stats.hot > 0 ? "warn" : "default"} />
            <HeroDivider />
            <div className="self-center">
              <Button
                size="sm"
                variant={showAIPanel ? "default" : "outline"}
                className="h-8 text-xs gap-1"
                onClick={() => setShowAIPanel(!showAIPanel)}
              >
                <Bot className="h-3 w-3" /> AI Assist
              </Button>
            </div>
          </>
        }
      />

      {/* ── KPI Strip ── */}
      <CockpitKpiStrip
        items={[
          { label: "Unread", value: stats.totalUnread, tone: stats.totalUnread > 0 ? "warn" : "default" },
          { label: "Hot", value: stats.hot, tone: stats.hot > 0 ? "warn" : "default" },
          { label: "Cold", value: stats.cold },
          { label: "Unresponsive", value: stats.unresponsive, tone: stats.unresponsive > 0 ? "bad" : "default" },
          { label: "Appt Set", value: stats.withAppt, tone: "good" },
          { label: "In Sequence", value: stats.automated },
          { label: "Avg Response", value: stats.avgResp > 0 ? `${stats.avgResp}m` : "—" },
        ]}
      />

      {/* ── Filter bar ── */}
      <CockpitFilterBar
        search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search conversations..." />}
        right={
          <>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as ConvoFilter)}>
              <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Conversations</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="hot">🔥 Hot</SelectItem>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="cold">❄️ Cold</SelectItem>
                <SelectItem value="unresponsive">Unresponsive</SelectItem>
                <SelectItem value="appointment_set">Appointment Set</SelectItem>
                <SelectItem value="no_response">No Response</SelectItem>
              </SelectContent>
            </Select>
            {!isRepRole && (
              <Select value={repFilter} onValueChange={setRepFilter}>
                <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="All Reps" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reps</SelectItem>
                  {reps.map((r) => <SelectItem key={r} value={r}>{repLabel(r)}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </>
        }
      />

      {/* Main Layout */}
      <div className={cn("grid gap-4 min-h-[600px]", showAIPanel && selected ? "grid-cols-1 lg:grid-cols-[1fr_2fr_280px]" : "grid-cols-1 lg:grid-cols-3")}>
        {/* Conversation List */}
        <div className="lg:col-span-1 border rounded-lg overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b bg-muted/30">
            <p className="text-xs font-medium text-muted-foreground">{filtered.length} conversations</p>
          </div>
          <div className="divide-y overflow-y-auto flex-1 max-h-[600px]">
            {filtered.map((conv) => {
              const engCfg = ENGAGEMENT_CONFIG[conv.engagement];
              const EngIcon = engCfg.icon;
              const seqActive = conv.sequenceStatus && !conv.sequenceStatus.isComplete;
              return (
                <button
                  key={conv.id}
                  onClick={() => setSelectedId(conv.id)}
                  className={cn(
                    "w-full text-left px-3 py-3 hover:bg-muted/50 transition-colors",
                    selectedId === conv.id && "bg-muted"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <EngIcon className={cn("h-3 w-3 shrink-0", engCfg.color)} />
                        <p className="text-sm font-medium text-foreground truncate">{conv.contactName}</p>
                        {conv.unreadCount > 0 && (
                          <Badge className="h-4 min-w-[16px] text-[9px] px-1 bg-primary">{conv.unreadCount}</Badge>
                        )}
                        {seqActive && (
                          <TooltipProvider><Tooltip><TooltipTrigger>
                            <Bot className="h-3 w-3 text-primary" />
                          </TooltipTrigger><TooltipContent><p className="text-xs">Automation sequence active</p></TooltipContent></Tooltip></TooltipProvider>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.lastMessagePreview}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-muted-foreground/60">{repLabel(conv.assignedRep)}</span>
                        {conv.hasAppointment && (
                          <Badge variant="outline" className="text-[8px] h-3.5 px-1 gap-0.5"><Calendar className="h-2.5 w-2.5" /> Appt</Badge>
                        )}
                        {seqActive && conv.sequenceStatus && (
                          <Badge variant="outline" className="text-[8px] h-3.5 px-1 gap-0.5 text-primary border-primary/20">
                            Step {(conv.sequenceStatus.currentStepIndex >= 0 ? conv.sequenceStatus.currentStepIndex + 1 : conv.sequenceStatus.steps.length)}/{conv.sequenceStatus.steps.length}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] text-muted-foreground">
                        {conv.lastContactAt ? formatTimeSince(conv.hoursSinceLastContact) : "—"}
                      </p>
                      <div className="flex items-center gap-1 mt-1 justify-end">
                        {conv.linkedLeadId && <Target className="h-3 w-3 text-muted-foreground/40" />}
                        {conv.linkedQuoteId && <FileText className="h-3 w-3 text-muted-foreground/40" />}
                        {conv.linkedJobId && <Briefcase className="h-3 w-3 text-muted-foreground/40" />}
                      </div>
                      <p className="text-[9px] text-muted-foreground/40 mt-0.5">{conv.messageCount} msgs</p>
                    </div>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <EmptyState
                icon={MessageSquare}
                title="No conversations"
                description="Customer conversations sync here from SMS, email, and portal messages."
                actionLabel="View Leads"
                onAction={() => navigate("/pipeline")}
                compact
              />
            )}
          </div>
        </div>

        {/* Thread + Intelligence */}
        <div className={cn(showAIPanel && selected ? "lg:col-span-1" : "lg:col-span-2", "border rounded-lg flex flex-col")}>
          {selected ? (
            <>
              {/* Thread Header with Intelligence */}
              <div className="px-4 py-3 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-foreground">{selected.contactName}</p>
                      <Badge variant="outline" className={cn("text-[9px] h-5 gap-1", ENGAGEMENT_CONFIG[selected.engagement].color)}>
                        {React.createElement(ENGAGEMENT_CONFIG[selected.engagement].icon, { className: "h-3 w-3" })}
                        {ENGAGEMENT_CONFIG[selected.engagement].label}
                      </Badge>
                      {selected.linkedStage && (
                        <Badge variant="outline" className="text-[9px] h-5">{selected.linkedStage}</Badge>
                      )}
                      {selected.sequenceStatus && (
                        <Badge variant="outline" className={cn("text-[9px] h-5 gap-1",
                          TONE_CONFIG[selected.sequenceStatus.profile.tone].color,
                          TONE_CONFIG[selected.sequenceStatus.profile.tone].bg
                        )}>
                          {TONE_CONFIG[selected.sequenceStatus.profile.tone].label}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {selected.contactPhone}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Last: {selected.lastContactAt ? formatTimeSince(selected.hoursSinceLastContact) : "Never"}</span>
                      {selected.lastInboundAt && (
                        <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> Reply: {formatTimeSince(selected.hoursSinceLastInbound)}</span>
                      )}
                      <span>{repLabel(selected.assignedRep)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1"
                      onClick={() => { setFollowUpDialog(selected.id); setFollowUpNotes(""); }}
                    >
                      <Zap className="h-3 w-3" /> Follow-Up
                    </Button>
                    {selected.linkedLeadId && (
                      <Link to={`/pipeline/${selected.linkedLeadId}`}>
                        <Badge variant="outline" className="text-[9px] gap-1 cursor-pointer hover:bg-muted"><Target className="h-3 w-3" /> Lead</Badge>
                      </Link>
                    )}
                    {selected.linkedQuoteId && (
                      <Link to={`/opportunities/${selected.linkedQuoteId}`}>
                        <Badge variant="outline" className="text-[9px] gap-1 cursor-pointer hover:bg-muted"><FileText className="h-3 w-3" /> Opportunity</Badge>
                      </Link>
                    )}
                    {selected.linkedJobId && (
                      <Link to={`/jobs/${selected.linkedJobId}`}>
                        <Badge variant="outline" className="text-[9px] gap-1 cursor-pointer hover:bg-muted"><Briefcase className="h-3 w-3" /> Job</Badge>
                      </Link>
                    )}
                  </div>
                </div>

                {/* Intelligence Strip */}
                <div className="flex items-center gap-4 mt-2 pt-2 border-t text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" /> {selected.messageCount} messages
                  </span>
                  {selected.responseTimeAvgMin > 0 && (
                    <span className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" /> Avg response: {selected.responseTimeAvgMin}m
                    </span>
                  )}
                  {selected.lastInboundAt && (
                    <span className="flex items-center gap-1">
                      <Send className="h-3 w-3" /> Last reply: {format(parseISO(selected.lastInboundAt), "MMM d, h:mm a")}
                    </span>
                  )}
                  {selected.sequenceStatus && !selected.sequenceStatus.isComplete && (
                    <span className="flex items-center gap-1 text-primary">
                      <Radio className="h-3 w-3" /> Sequence active — {MODE_CONFIG[selected.sequenceStatus.profile.automationMode].label}
                    </span>
                  )}
                </div>
              </div>

              <ConversationThread
                conversationId={selected.id}
                contactName={selected.contactName}
                contactPhone={selected.contactPhone}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-20" />
                <p className="text-sm">Select a conversation</p>
                <p className="text-xs mt-1 text-muted-foreground/60">Choose from the list to view messages</p>
              </div>
            </div>
          )}
        </div>

        {/* AI Intelligence Panel */}
        {showAIPanel && selected && (
          <div className="border rounded-lg overflow-hidden flex flex-col max-h-[700px] overflow-y-auto">
            <div className="px-3 py-2 border-b bg-muted/30">
              <p className="text-xs font-semibold flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-primary" /> AI Assistant</p>
            </div>

            <div className="p-3 space-y-3">
              {/* Sequence Status */}
              {selected.sequenceStatus && (
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold text-foreground flex items-center gap-1">
                    <Radio className="h-3 w-3" /> Sequence: {selected.sequenceStatus.source}
                  </p>
                  <div className="space-y-1">
                    {selected.sequenceStatus.steps.map((s, i) => (
                      <div key={s.step.id} className={cn(
                        "flex items-center gap-2 text-[10px] py-1 px-2 rounded",
                        s.status === "completed" && "bg-success/10 text-success",
                        s.status === "pending" && "bg-warning/10 text-warning font-medium",
                        s.status === "skipped" && "bg-muted text-muted-foreground line-through",
                        s.status === "scheduled" && "text-muted-foreground",
                      )}>
                        {s.status === "completed" && <CheckCircle className="h-3 w-3 shrink-0" />}
                        {s.status === "pending" && <Play className="h-3 w-3 shrink-0" />}
                        {s.status === "skipped" && <Pause className="h-3 w-3 shrink-0" />}
                        {s.status === "scheduled" && <Clock className="h-3 w-3 shrink-0" />}
                        <span className="flex-1 truncate">{s.step.name}</span>
                        <span className="uppercase text-[8px] opacity-60">{s.step.channel}</span>
                      </div>
                    ))}
                  </div>
                  {selected.sequenceStatus.hasCustomerReplied && (
                    <div className="flex items-center gap-1 text-[10px] text-success bg-success/10 rounded px-2 py-1">
                      <CheckCircle className="h-3 w-3" /> Customer responded — remaining steps paused
                    </div>
                  )}
                  {selected.sequenceStatus.isComplete && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted rounded px-2 py-1">
                      <CheckCircle className="h-3 w-3" /> Sequence complete
                    </div>
                  )}
                </div>
              )}

              {/* AI Suggested Response */}
              {currentAISuggestion && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-semibold text-foreground flex items-center gap-1">
                      <Bot className="h-3 w-3" /> Suggested Response
                    </p>
                    <Badge variant="outline" className="text-[8px] h-4 px-1">{currentAISuggestion.confidence}% match</Badge>
                  </div>

                  <div className="border rounded-lg p-2.5 bg-card">
                    {isEditing ? (
                      <Textarea
                        value={editedMessage}
                        onChange={(e) => setEditedMessage(e.target.value)}
                        rows={5}
                        className="text-xs"
                      />
                    ) : (
                      <p className="text-xs text-foreground whitespace-pre-wrap leading-relaxed">{currentAISuggestion.message}</p>
                    )}
                  </div>

                  <p className="text-[9px] text-muted-foreground italic">{currentAISuggestion.reasoning}</p>

                  <div className="flex items-center gap-1.5">
                    <Button size="sm" className="h-6 text-[10px] gap-1 flex-1" onClick={handleSendAISuggestion}>
                      <Send className="h-3 w-3" /> Send
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={() => {
                      if (isEditing) { setIsEditing(false); }
                      else { setEditedMessage(currentAISuggestion.message); setIsEditing(true); }
                    }}>
                      <Edit3 className="h-3 w-3" /> {isEditing ? "Preview" : "Edit"}
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={handleCopyMessage}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Booking Optimization */}
              {bookingOpt && (
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold text-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Booking Intelligence
                  </p>

                  <div className="border rounded-lg p-2.5 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-muted-foreground">No-Show Risk</span>
                      <Badge variant="outline" className={cn("text-[9px] h-4",
                        bookingOpt.noShowRisk === "high" && "text-destructive border-destructive/30",
                        bookingOpt.noShowRisk === "medium" && "text-warning border-warning/30",
                        bookingOpt.noShowRisk === "low" && "text-success border-success/30",
                      )}>
                        {bookingOpt.noShowRisk.toUpperCase()}
                      </Badge>
                    </div>

                    {bookingOpt.confirmationNeeded && (
                      <div className="flex items-center gap-1 text-[10px] text-warning bg-warning/10 rounded px-2 py-1">
                        <AlertTriangle className="h-3 w-3" /> Confirmation recommended
                      </div>
                    )}

                    <div>
                      <p className="text-[10px] text-muted-foreground mb-1">Suggested Times</p>
                      <div className="space-y-0.5">
                        {bookingOpt.suggestedTimes.map((t, i) => (
                          <button key={i} className="w-full text-left text-[10px] px-2 py-1 rounded hover:bg-muted transition-colors flex items-center gap-1.5">
                            <Calendar className="h-3 w-3 text-primary" />
                            <span>{t}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] text-muted-foreground mb-1">Reminders</p>
                      <div className="flex flex-wrap gap-1">
                        {bookingOpt.reminderSchedule.map((r, i) => (
                          <Badge key={i} variant="outline" className="text-[8px] h-4">{r}</Badge>
                        ))}
                      </div>
                    </div>

                    <p className="text-[9px] text-muted-foreground italic mt-1">{bookingOpt.engagementTip}</p>
                  </div>
                </div>
              )}

              {/* Automation Mode Indicator */}
              {selected.sequenceStatus && (
                <div className="border rounded-lg p-2.5">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-muted-foreground">Mode</p>
                    <Badge variant="outline" className="text-[9px] h-4 gap-1">
                      <Shield className="h-2.5 w-2.5" />
                      {MODE_CONFIG[selected.sequenceStatus.profile.automationMode].label}
                    </Badge>
                  </div>
                  <p className="text-[9px] text-muted-foreground mt-1">
                    {MODE_CONFIG[selected.sequenceStatus.profile.automationMode].description}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Follow-Up Dialog */}
      <Dialog open={!!followUpDialog} onOpenChange={() => setFollowUpDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Create Follow-Up Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {selected && (
              <div className="text-sm">
                <p className="font-medium">{selected.contactName}</p>
                <p className="text-xs text-muted-foreground">{selected.contactPhone} · {repLabel(selected.assignedRep)}</p>
              </div>
            )}
            <Textarea
              value={followUpNotes}
              onChange={(e) => setFollowUpNotes(e.target.value)}
              placeholder="Follow-up notes..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowUpDialog(null)}>Cancel</Button>
            <Button onClick={handleCreateFollowUp}>Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CockpitPage>
  );
}

/* ═══════ MINI KPI ═══════ */

function MiniKPI({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <div className="border rounded-lg px-3 py-2 text-center">
      <p className={cn("text-base font-bold", accent)}>{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}
